<template>
  <div class="page">
    <xh-header />

    <div class="container-width study-detail" v-if="course">
      <div class="bread">
        <span class="mouse" @click="goBack">
          <i class="el-icon-arrow-left"></i> 返回个人中心
        </span>
      </div>

      <!-- 课程概览 -->
      <div class="overview">
        <div class="ov-head">
          <h2>{{ course.courseName }}</h2>
          <el-tag v-if="finished" type="success" effect="dark">已完成</el-tag>
          <el-tag v-else type="warning" effect="plain">进行中</el-tag>
        </div>
        <div class="ov-meta" v-if="course.startTime || course.endTime">
          <i class="el-icon-time"></i>
          学习周期：
          {{ course.startTime ? formatDate(Date.parse(course.startTime), 'YYYY-mm-dd') : '--' }}
          ~
          {{ course.endTime ? formatDate(Date.parse(course.endTime), 'YYYY-mm-dd') : '--' }}
        </div>
        <div class="ov-stat">
          <div class="stat-item">
            <div class="num"><span :class="{'green': videoDone}">{{ watchedNum }}</span> / {{ totalVideoNum }}</div>
            <div class="lab">视频已学 / 总数</div>
          </div>
          <div class="stat-item">
            <div class="num">
              <span v-if="passStatus.latestPassFlag === 1" class="green">已通过</span>
              <span v-else-if="passStatus.latestPassFlag === 0" class="red">未通过</span>
              <span v-else class="gray">未参考</span>
            </div>
            <div class="lab">考试状态<span v-if="passStatus.attemptCount">（已考 {{ passStatus.attemptCount }} 次）</span></div>
          </div>
          <div class="stat-item">
            <div class="num">{{ totalQuestionNum }}</div>
            <div class="lab">考试题目总数</div>
          </div>
        </div>
        <div class="ov-tip" v-if="course.remark">
          <i class="el-icon-info"></i> {{ course.remark }}
        </div>
        <div class="ov-tip pass-rule">
          <i class="el-icon-medal"></i>
          及格规则：{{ passRuleText }}
        </div>

        <!-- 已完成：培训证书入口 -->
        <div class="cert-banner" v-if="finished">
          <div class="cb-text">
            <i class="el-icon-trophy"></i>
            <div>
              <div class="cb-title">恭喜完成本课程学习！</div>
              <div class="cb-sub">您已获得《{{ course.courseName }}》培训结业证书，可查看并保存到本地。</div>
            </div>
          </div>
          <el-button type="primary" size="small" @click="goCertificate">
            <i class="el-icon-document"></i> 查看 / 下载证书
          </el-button>
        </div>
      </div>

      <!-- 视频区 -->
      <div class="block">
        <div class="block-title">
          <span><i class="el-icon-video-play"></i> 课程视频</span>
          <span class="sub">看完所有视频即视为「视频学习完成」</span>
        </div>
        <div v-if="videos.length" class="video-list">
          <div class="video-item" v-for="(v, idx) in videos" :key="v.id">
            <div class="vi-idx">{{ String(idx + 1).padStart(2, '0') }}</div>
            <div class="vi-main">
              <div class="vi-title">
                {{ v.title }}
                <el-tag size="mini" type="info" v-if="v.category">{{ categoryText(v.category) }}</el-tag>
              </div>
              <div class="vi-desc" v-if="v.videoDuration">
                <i class="el-icon-time"></i> 时长 {{ formatDuration(v.videoDuration) }}
              </div>
            </div>
            <div class="vi-status">
              <el-tag size="small" v-if="watchedSet[v.id]" type="success">已观看</el-tag>
              <el-tag size="small" v-else type="info">未观看</el-tag>
            </div>
            <div class="vi-action">
              <el-button
                size="small"
                :type="watchedSet[v.id] ? 'default' : 'primary'"
                @click="goVideo(v.id)">
                {{ watchedSet[v.id] ? '重新观看' : '开始观看' }}
              </el-button>
            </div>
          </div>
        </div>
        <div v-else class="empty-block">本课程暂未上传视频</div>
      </div>

      <!-- 考试区 -->
      <div class="block">
        <div class="block-title">
          <span><i class="el-icon-document-checked"></i> 课程考试</span>
          <span class="sub">通过考试方可获得「已完成」标识</span>
        </div>
        <div v-if="totalQuestionNum > 0" class="exam-card">
          <div class="ec-row">
            <div>
              <div class="ec-num">{{ totalQuestionNum }}</div>
              <div class="ec-lab">题目数量</div>
            </div>
            <div>
              <div class="ec-num">
                <span v-if="passStatus.latestPassFlag === 1" class="green">已通过</span>
                <span v-else-if="passStatus.latestPassFlag === 0" class="red">未通过</span>
                <span v-else class="gray">未参考</span>
              </div>
              <div class="ec-lab">当前状态</div>
            </div>
            <div>
              <div class="ec-num">{{ passStatus.latestScore !== null && passStatus.latestScore !== undefined ? passStatus.latestScore : '--' }}</div>
              <div class="ec-lab">最近一次得分</div>
            </div>
            <div>
              <div class="ec-num">{{ passStatus.attemptCount || 0 }}</div>
              <div class="ec-lab">已考次数</div>
            </div>
          </div>
          <div class="ec-actions">
            <template v-if="passStatus.latestPassFlag === 1">
              <el-button type="primary" @click="goRecord()">查看答题记录</el-button>
              <el-button @click="goExam">再考一次</el-button>
            </template>
            <template v-else-if="passStatus.latestPassFlag === 0">
              <el-button type="primary" @click="goExam">重新答题</el-button>
              <el-button @click="goRecord()">查看上次记录</el-button>
            </template>
            <template v-else>
              <el-button type="primary" @click="goExam">开始答题</el-button>
            </template>
          </div>
        </div>
        <div v-else class="empty-block">本课程暂未配置考试题目</div>
      </div>
    </div>

    <div v-else-if="loaded" class="container-width">
      <div class="empty-block">课程不存在或已下架 <el-button size="small" @click="goBack">返回</el-button></div>
    </div>

    <xh-footer />
  </div>
</template>

<script>
import XhHeader from '../../../reveals/xh-header.vue'
import XhFooter from '../../../reveals/xh-footer.vue'
import {
  getStudyCourseDetail,
  getStudyVideoList,
  getStudyExamQuestions,
  getStudyUserPassStatus,
  getStudyWatchPage
} from '@/service/apis/study'

const CATEGORY_MAP = {
  1: '沙滩排球裁判',
  2: '室内排球裁判',
  3: '气排球裁判',
  4: '教练员内容'
}

export default {
  name: 'StudyCourseDetail',
  components: { XhHeader, XhFooter },
  data () {
    return {
      account: null,
      courseId: null,
      course: null,
      videos: [],
      watchedSet: {},   // {videoId: true}
      totalQuestionNum: 0,
      passStatus: {
        attemptCount: 0,
        latestScore: null,
        latestPassFlag: null,
        lastSubmitTime: null
      },
      loaded: false
    }
  },
  computed: {
    watchedNum () {
      return Object.keys(this.watchedSet).length
    },
    totalVideoNum () {
      return this.videos.length
    },
    videoDone () {
      return this.totalVideoNum === 0 ? true : this.watchedNum >= this.totalVideoNum
    },
    examDone () {
      return this.passStatus.latestPassFlag === 1
    },
    finished () {
      return this.videoDone && this.examDone && (this.totalVideoNum > 0 || this.totalQuestionNum > 0)
    },
    passRuleText () {
      if (this.course && this.course.passRightNum > 0) {
        return `答对题数 ≥ ${ this.course.passRightNum } 题视为通过`
      }
      return '得分 ≥ 60 分视为通过'
    }
  },
  created () {
    const id = this.$route.query.id
    if (!id) {
      this.$message.error('参数缺失')
      this.$router.replace('/userinfo')
      return
    }
    this.courseId = Number(id)
    const acct = localStorage.getItem('ACCOUNT')
    if (!acct) {
      this.$router.replace('/login')
      return
    }
    this.account = JSON.parse(acct)
    this.loadAll()
  },
  methods: {
    categoryText (c) {
      return CATEGORY_MAP[c] || ''
    },
    formatDuration (sec) {
      sec = Number(sec) || 0
      const m = Math.floor(sec / 60)
      const s = sec % 60
      return `${ m }分${ String(s).padStart(2, '0') }秒`
    },
    handle9994 (res) {
      if (res && res.errCode === 9994) {
        localStorage.clear()
        this.$router.replace('/login')
        return true
      }
      return false
    },
    loadAll () {
      // 课程详情
      getStudyCourseDetail.call(this, { id: this.courseId }).then(res => {
        if (this.handle9994(res)) return
        if (res.errCode === 0) {
          this.course = res.data
        }
        this.loaded = true
      })
      // 视频清单（兼容 IPage 与数组两种返回结构）
      getStudyVideoList.call(this, { courseId: this.courseId, pageIndex: 1, pageSize: 1000 }).then(res => {
        if (this.handle9994(res)) return
        if (res.errCode === 0 && res.data) {
          if (Array.isArray(res.data)) {
            this.videos = res.data
          } else if (Array.isArray(res.data.records)) {
            this.videos = res.data.records
          }
        }
      })
      // 题库题量（pageSize=1 仅取 total）
      getStudyExamQuestions.call(this, { courseId: this.courseId, pageIndex: 1, pageSize: 1 }).then(res => {
        if (this.handle9994(res)) return
        if (res.errCode === 0 && res.data) {
          this.totalQuestionNum = res.data.total || 0
        }
      })
      // 当前用户已看视频集合
      getStudyWatchPage.call(this, {
        accountId: this.account.id,
        courseId: this.courseId,
        pageIndex: 1,
        pageSize: 1000
      }).then(res => {
        if (this.handle9994(res)) return
        if (res.errCode === 0 && res.data && Array.isArray(res.data.records)) {
          const set = {}
          res.data.records.forEach(r => {
            if (r.finishFlag === 1 || r.finishFlag === undefined) {
              set[r.videoId] = true
            }
          })
          this.watchedSet = set
        }
      })
      // 考试通过状态
      getStudyUserPassStatus.call(this, { accountId: this.account.id }).then(res => {
        if (this.handle9994(res)) return
        if (res.errCode === 0 && Array.isArray(res.data)) {
          const s = res.data.find(x => x.courseId === this.courseId)
          if (s) {
            this.passStatus = {
              attemptCount: s.attemptCount || 0,
              latestScore: s.latestScore,
              latestPassFlag: s.latestPassFlag,
              lastSubmitTime: s.lastSubmitTime
            }
          }
        }
      })
    },
    goVideo (videoId) {
      this.$router.push(`/study/video?id=${ videoId }&courseId=${ this.courseId }`)
    },
    goExam () {
      this.$router.push(`/study/exam?courseId=${ this.courseId }`)
    },
    goRecord () {
      this.$router.push(`/study/exam-record?courseId=${ this.courseId }`)
    },
    goCertificate () {
      this.$router.push(`/study/certificate?courseId=${ this.courseId }`)
    },
    goBack () {
      this.$router.push('/userinfo')
    }
  }
}
</script>

<style scoped>
.study-detail {
  padding: 20px 0 60px;
  min-height: 60vh;
}
.bread {
  font-size: 13px;
  color: #888;
  padding: 10px 0;
}
.bread .mouse:hover {
  color: #4c2ac9;
}
.overview {
  border: solid 1px #ece8ff;
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, #faf8ff 0%, #fff 60%);
}
.ov-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ov-head h2 {
  font-size: 20px;
  color: #2e2e2e;
  font-weight: 600;
}
.ov-meta {
  font-size: 13px;
  color: #888;
  margin-top: 8px;
}
.ov-meta i { margin-right: 4px; }
.ov-stat {
  display: flex;
  margin-top: 18px;
  border-top: dashed 1px #ece8ff;
  padding-top: 18px;
}
.ov-stat .stat-item {
  flex: 1;
  text-align: center;
  border-right: solid 1px #f0ebff;
}
.ov-stat .stat-item:last-child {
  border-right: none;
}
.ov-stat .num {
  font-size: 22px;
  color: #4c2ac9;
  font-weight: 600;
  line-height: 30px;
}
.ov-stat .lab {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
.ov-tip {
  margin-top: 14px;
  font-size: 13px;
  color: #666;
  background: #f8f6ff;
  padding: 8px 12px;
  border-radius: 4px;
  line-height: 22px;
}
.ov-tip i { margin-right: 4px; color: #4c2ac9; }
.ov-tip.pass-rule { background: #fff7e6; }
.ov-tip.pass-rule i { color: #f0a020; }

/* 培训证书入口横幅 */
.cert-banner {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 8px;
  background: linear-gradient(135deg, #fff8e6 0%, #fff3d6 100%);
  border: solid 1px #ffe2a8;
}
.cert-banner .cb-text {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cert-banner .cb-text > i {
  font-size: 34px;
  color: #f0a020;
}
.cert-banner .cb-title {
  font-size: 15px;
  font-weight: 600;
  color: #8a5a00;
}
.cert-banner .cb-sub {
  font-size: 12px;
  color: #b3822e;
  margin-top: 2px;
  line-height: 18px;
}

.green { color: #4caf50 !important; }
.red { color: #f56c6c !important; }
.gray { color: #999 !important; }

.block {
  border: solid 1px #ece8ff;
  border-radius: 10px;
  margin-bottom: 18px;
  overflow: hidden;
}
.block-title {
  font-size: 16px;
  color: #2e2e2e;
  font-weight: 600;
  padding: 14px 20px;
  border-bottom: solid 1px #f0ebff;
  background: #faf8ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.block-title i { color: #4c2ac9; margin-right: 6px; }
.block-title .sub {
  font-size: 12px;
  color: #999;
  font-weight: normal;
}
.video-list { padding: 6px 0; }
.video-item {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  border-bottom: solid 1px #f5f5f5;
}
.video-item:last-child { border-bottom: none; }
.vi-idx {
  width: 40px;
  font-size: 18px;
  color: #ddd;
  font-weight: 600;
  font-family: 'Helvetica';
}
.vi-main { flex: 1; }
.vi-title {
  font-size: 14px;
  color: #333;
  line-height: 22px;
}
.vi-title .el-tag { margin-left: 8px; }
.vi-desc {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
.vi-status { width: 80px; text-align: center; }
.vi-action { width: 110px; text-align: right; }

.exam-card { padding: 20px 24px; }
.ec-row {
  display: flex;
  text-align: center;
}
.ec-row > div {
  flex: 1;
  border-right: solid 1px #f0ebff;
}
.ec-row > div:last-child { border-right: none; }
.ec-num {
  font-size: 22px;
  color: #4c2ac9;
  font-weight: 600;
  line-height: 30px;
}
.ec-lab {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
.ec-actions {
  margin-top: 22px;
  text-align: center;
}
.empty-block {
  text-align: center;
  color: #999;
  padding: 40px 20px;
  font-size: 14px;
}

@media screen and (max-width: 800px) {
  .study-detail { padding: 12px 16px 30px; }
  .overview { padding: 16px; }
  .ov-head h2 { font-size: 17px; }
  .ov-stat { flex-wrap: wrap; }
  .ov-stat .stat-item {
    flex: 0 0 50%;
    border-right: none;
    margin-bottom: 12px;
  }
  .ov-stat .num { font-size: 18px; }
  .video-item {
    flex-wrap: wrap;
    padding: 12px 14px;
  }
  .vi-idx { display: none; }
  .vi-main { flex: 1 0 100%; margin-bottom: 8px; }
  .vi-status { width: auto; flex: 1; text-align: left; }
  .vi-action { width: auto; }
  .block-title { padding: 12px 14px; font-size: 14px; }
  .block-title .sub { display: none; }
  .ec-row { flex-wrap: wrap; }
  .ec-row > div { flex: 0 0 50%; border-right: none; margin-bottom: 12px; }
  .ec-num { font-size: 18px; }
  .ec-actions .el-button { margin: 4px 4px; }
}
</style>
